import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ShoppingCart, User, LogOut, Shield, Trash2, ArrowLeft, ArrowRight, Home 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";
const DEFAULT_PRODUCT_IMAGE = "https://static.prod-images.emergentagent.com/jobs/d742bfc9-e565-49d9-b972-9ec11d4b1acf/images/8bffc3d80961a69d81ff6049f0b0e3b52871da1a055b9513c572b6a653fd3377.png";

// Helper to resolve image URLs - handles both relative and absolute URLs
const resolveImageUrl = (url) => {
  if (!url || url.trim() === '') return DEFAULT_PRODUCT_IMAGE;
  
  // If it's a broken old preview URL, return default immediately
  if (url.includes('preview.emergentagent.com') && !url.includes('rare-deck-hub')) {
    console.warn('Broken preview URL detected:', url);
    return DEFAULT_PRODUCT_IMAGE;
  }
  
  // If already absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  
  // If relative URL, prepend backend URL
  const backendBase = process.env.REACT_APP_BACKEND_URL;
  return `${backendBase}${url}`;
};

export default function Cart() {
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();
  const { cart, itemCount, removeFromCart, loading } = useCart();

  // BLKCRD discount preview — mirrors Checkout.js auto-apply logic so the
  // member sees the same 15% off on the cart page that they'd see at checkout.
  // Excludes gift cards, BLKCRD memberships, auctions, and trade-ins.
  const eligibleSubtotal = cart.items.reduce((sum, item) => {
    if (item.is_gift_card || item.is_blkcrd || item.is_bidding || item.is_trade) return sum;
    return sum + (item.item_total || 0);
  }, 0);
  const blkcrdActive = !!userProfile?.has_blkcrd && eligibleSubtotal > 0;
  const blkcrdDiscount = blkcrdActive ? eligibleSubtotal * 0.15 : 0;
  const cartTotalAfter = (cart.total || 0) - blkcrdDiscount;

  const getItemImage = (item) => {
    return resolveImageUrl(item.image_url);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link 
              to="/catalog"
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="home-link"
            >
              <Home className="w-5 h-5" />
            </Link>
            <Link 
              to="/cart" 
              className="relative p-2 text-tiffany-500"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link 
              to="/dashboard" 
              className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
              data-testid="dashboard-link"
            >
              <User className="w-5 h-5" />
            </Link>
            {user?.is_admin && (
              <Link 
                to="/admin" 
                className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                data-testid="admin-link"
              >
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <button 
              onClick={logout}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto">
        {/* Back link */}
        <Link 
          to="/catalog"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-tiffany-500 font-mono text-sm mb-8 transition-colors"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="w-4 h-4" />
          CONTINUE SHOPPING
        </Link>

        <h1 className="font-sans text-4xl md:text-5xl text-white mb-8 tracking-tighter" data-testid="cart-title">
          YOUR CART
        </h1>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-tiffany-500 font-mono cursor-blink">LOADING</div>
          </div>
        ) : cart.items.length === 0 ? (
          <div className="text-center py-20 border border-zinc-800">
            <ShoppingCart className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 font-mono mb-6">YOUR CART IS EMPTY</p>
            <Button
              onClick={() => navigate('/catalog')}
              className="bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none"
              data-testid="shop-now-btn"
            >
              SHOP NOW
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cart Items */}
            <div className="border border-zinc-800 divide-y divide-zinc-800" data-testid="cart-items">
              {cart.items.map((item) => (
                <div 
                  key={item.product_id} 
                  className="p-4 md:p-6 flex gap-4 md:gap-6"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <div className="w-20 h-20 md:w-24 md:h-24 bg-zinc-900 flex-shrink-0">
                    <img
                      src={getItemImage(item)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = DEFAULT_PRODUCT_IMAGE;
                      }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    {item.brand && (
                      <span className="font-mono text-[10px] text-tiffany-500 uppercase tracking-wider">
                        {item.brand}
                      </span>
                    )}
                    <Link 
                      to={`/product/${item.product_id}`}
                      className="font-sans text-lg text-white hover:text-tiffany-500 transition-colors block uppercase"
                      data-testid={`cart-item-name-${item.product_id}`}
                    >
                      {item.name}
                    </Link>
                    {item.variant_name && (
                      <p className="font-mono text-sm text-tiffany-500 mt-0.5 uppercase">
                        {item.variant_name}
                      </p>
                    )}
                    <p className="font-mono text-sm text-zinc-500 mt-1">
                      QTY: {item.quantity}
                    </p>
                    {item.is_bid_win && (
                      <span className="inline-block bg-tiffany-500/20 text-tiffany-500 font-mono text-xs px-2 py-0.5 mt-2">
                        BID WIN
                      </span>
                    )}
                    {item.is_trade && (
                      <div className="mt-2">
                        <span className="inline-block bg-purple-500/20 text-purple-400 font-mono text-xs px-2 py-0.5">
                          TRADE-IN DEAL
                        </span>
                        {item.trade_item_name && (
                          <p className="font-mono text-[10px] text-zinc-500 mt-1">
                            TRADING: {item.trade_item_name}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Price & Remove */}
                  <div className="flex flex-col items-end justify-between">
                    <div className="text-right">
                      <span className="font-mono text-lg text-white">
                        ${item.item_total.toFixed(2)}
                      </span>
                      {/* Show original price crossed out for trade deals */}
                      {item.is_trade && item.original_price && item.original_price !== item.price && (
                        <p className="font-mono text-xs text-zinc-500 line-through">
                          ${item.original_price.toFixed(2)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product_id, item.variant_id)}
                      className="p-2 text-zinc-500 hover:text-red-500 transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="border border-zinc-800 p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-mono text-zinc-500">SUBTOTAL</span>
                <span className="font-mono text-xl text-white" data-testid="cart-subtotal">
                  ${cart.total.toFixed(2)}
                </span>
              </div>

              {blkcrdActive && (
                <div className="flex justify-between items-center" data-testid="cart-blkcrd-discount">
                  <span className="font-mono text-purple-400 text-sm tracking-wider">
                    BLKCRD MEMBER (-15%)
                  </span>
                  <span className="font-mono text-purple-500">
                    -${blkcrdDiscount.toFixed(2)}
                  </span>
                </div>
              )}

              {blkcrdActive && (
                <div className="flex justify-between items-center pt-3 border-t border-zinc-800">
                  <span className="font-mono text-zinc-300">ESTIMATED TOTAL</span>
                  <span className="font-mono text-2xl text-purple-500" data-testid="cart-total">
                    ${cartTotalAfter.toFixed(2)}
                  </span>
                </div>
              )}

              <p className="font-mono text-xs text-zinc-600">
                SHIPPING CALCULATED AT CHECKOUT
              </p>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full h-14 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans text-lg tracking-widest rounded-none flex items-center justify-center gap-2"
                data-testid="checkout-btn"
              >
                PROCEED TO CHECKOUT
                <ArrowRight className="w-5 h-5" />
              </Button>

              <p className="font-mono text-xs text-zinc-500 text-center">
                PAYMENT VIA PAYPAL F&F
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
